import * as React from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import { AppBar } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import Performance from "./DashboardMobileModules/Performance/Performance";
import OverviewMobile from "./DashboardMobileModules/Overview/OverviewMobile";
import AnomaliesMobile from "./DashboardMobileModules/Anomalies/AnomaliesMobile";
import {
  spacing,
  typography,
  colors,
} from "../../../../../assets/styles/tokens";
import BudgetControl from "./DashboardMobileModules/BudgetControl/BudgetControl";
import BeAware from "./DashboardMobileModules/BeAware/BeAware";

const tabConfig = [
  { label: "Performance", value: "performance", trackerId: "643" },
  { label: "Budget Control", value: "budgetControl", trackerId: "647" },
  { label: "Overview", value: "overview", trackerId: "649" },
  { label: "BeAware", value: "beAware", trackerId: "662" },
  { label: "Anomalies", value: "anomalies", trackerId: "657" },
];

const TAB_COMPONENTS = [
  Performance,
  BudgetControl,
  OverviewMobile,
  BeAware,
  AnomaliesMobile,
];

interface TabPanelProps {
  children?: React.ReactNode;
  activeIndex: number;
  index: number;
  visited: boolean;
}

// Only renders children if tab has been visited at least once
// Once visited, keeps mounted with display:none (no re-mount, no re-fetch)
const TabPanel = React.memo(
  ({ children, activeIndex, index, visited }: TabPanelProps) => {
    if (!visited) return null; // never visited → don't mount → no API call
    return (
      <Box sx={{ display: activeIndex === index ? "block" : "none", pt: 1 }}>
        {children}
      </Box>
    );
  },
);

export default function DashboardMobileTabs() {
  const [searchParams, setSearchParams] = useSearchParams();

  const currentTabParam = searchParams.get("tabName");
  const initialIndex = tabConfig.findIndex(
    (tab) => tab.value === currentTabParam,
  );
  const activeIndex = initialIndex !== -1 ? initialIndex : 0;

  const [value, setValue] = React.useState(activeIndex);

  // Track which tabs have been visited — only visited tabs are mounted
  // On refresh: only the active tab index is pre-visited
  const workspaceId = searchParams.get("workspace");
  const [visitedTabs, setVisitedTabs] = React.useState<Set<number>>(
    () => new Set([activeIndex]),
  );

  // Reset visited tabs when workspace changes
  const prevWorkspaceRef = React.useRef(workspaceId);
  if (prevWorkspaceRef.current !== workspaceId) {
    prevWorkspaceRef.current = workspaceId;
    setVisitedTabs(new Set([value])); // ← runs during render, before effects
  }

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    // Mark this tab as visited so it mounts for the first time
    setVisitedTabs((prev) => new Set(prev).add(newValue));
    const newTabValue = tabConfig[newValue].value;
    searchParams.set("tabName", newTabValue);
    setSearchParams(searchParams);
  };

  return (
    <Box sx={{ height: "93vh", overflowY: "auto" }}>
      <AppBar
        position="sticky"
        sx={{
          bgcolor: "background.paper",
          boxShadow: "0px 1px 2px #0A0D120F , 0px 1px 3px #0A0D121A",
        }}
      >
        <Tabs
          value={value}
          onChange={handleChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            "& .MuiTabs-scrollButtons": {
              color: colors.GrayLightMode[500].$value,
            },
          }}
          TabIndicatorProps={{
            sx: {
              backgroundColor: colors.Brand[600].$value,
              height: spacing.Spacing.xxs.$value,
            },
          }}
        >
          {tabConfig.map((tab, index) => (
            <Tab
              key={tab.value}
              label={tab.label}
              disableRipple
              disabled={index !== 0 && index !== 2 && index !== 1 &&  index !== 4 && index !== 3}
              {...(tab.trackerId && value === index
                ? { "data-tracker": tab.trackerId }
                : {})}
              sx={{
                color: colors.GrayLightMode[500].$value,
                textTransform: "none",
                fontSize: typography.Size.Text.sm.$value,
                lineHeight: typography.Line_height.text.sm.$value,
                fontWeight: typography.Font_weight.Medium.$value,
                "&.Mui-selected": {
                  color: colors.GrayLightMode[900].$value,
                },
                "&:hover": { backgroundColor: "transparent" },
                "&:first-of-type": {
                  marginLeft: spacing.Spacing["6xl"].$value,
                },
              }}
            />
          ))}
        </Tabs>
      </AppBar>

      {TAB_COMPONENTS.map((Component, index) => (
        <TabPanel
          key={tabConfig[index].value}
          activeIndex={value}
          index={index}
          visited={visitedTabs.has(index)}
        >
          <Component />
        </TabPanel>
      ))}
    </Box>
  );
}
